import { isUndefined } from 'lodash';
import { getClassName, getPrevYearColumn } from 'pages/Financials/utilities';
import { getNumberValue, getValueByPropOrFallback, parseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { KPI_DATA_ROW_START_INDEX } from './constants';
import { formatMap } from './rowConfig';
import { GetKPIAndColumnValueParams, KpiCell, KpiColumn, KpiParser } from './types';

const getKpiFromKpiDataset = (column: KpiColumn, rowIndex: number) => {
  const { kpi_dataset: kpiDataset } = column;

  if (!kpiDataset?.length) {
    return null;
  }

  const kpiData = kpiDataset.find(kpi => kpi.company_kpi.sort_order === rowIndex - KPI_DATA_ROW_START_INDEX);

  if (!kpiData) {
    return null;
  }

  return kpiData;
};

const getKPIAndColumnValue = (params: GetKPIAndColumnValueParams) => {
  const { column, row, index } = params;
  const kpi = getKpiFromKpiDataset(column, index);
  const columnValue = index >= KPI_DATA_ROW_START_INDEX && kpi ? kpi.kpi_value : column[row.alias as keyof KpiColumn];
  return {
    columnValue,
    kpi,
  };
};

const parser: KpiParser = async params => {
  const { columns, rowConfig, currencyCode } = params;
  const cells: { [key: string]: KpiCell } = {};
  const alphabet = alphabetGenerator([], columns.length);

  columns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];

    rowConfig.forEach((row, index) => {
      const rowNumber = index + 1;
      const key = columnLegend + rowNumber;
      const type = getValueByPropOrFallback(row, 'gridType', null);
      const { kpi, columnValue } = getKPIAndColumnValue({ column, row, index });
      const value = !isUndefined(columnValue) ? columnValue : null;
      const parsedValue = parseValue(value, type, null, null, row.dbType);

      const date = getValueByPropOrFallback(column, 'statement_date', column.date);

      const { needs_actualization } = column;
      const className = getClassName(Boolean(needs_actualization), row);

      const isAdditionalHistoricalYear = Boolean(column.isAdditionalHistoricalYear);
      const showAdditionalHistoricalYear = Boolean(column.showAdditionalHistoricalYear);
      const isVisibleColumn = isAdditionalHistoricalYear
        ? isAdditionalHistoricalYear && showAdditionalHistoricalYear
        : null;

      cells[key] = {
        ...row,
        isEditableTitleCell: false,
        date,
        key,
        columnLegend,
        columnIndex,
        needs_actualization,
        className,
        columnId: column.id,
        rowNumber,
        value: parsedValue,
        alias: getValueByPropOrFallback(row, 'alias', ''),
        isParent: Boolean(column.isParent),
        parentColumn: column.parentColumn,
        rowSpan: !column.isProjectedYear && index === 0 ? 2 : 1,
        prevYear: getPrevYearColumn({ columnIndex, column, alphabet }),
        isProjectionPeriod: Boolean(column.isProjectedYear),
        isParentWithDivider: Boolean(column.isParentWithDivider),
        needsSpacer: Boolean(column.needsSpacer),
        isAdditionalHistoricalYear,
        showAdditionalHistoricalYear,
        isVisibleColumn,
        mdMatchesFiscalYearEnd: column.mdMatchesFiscalYearEnd,
        year: column.year,
        style: row.style,
        currencyCode,
        kpiId: kpi?.id ?? 0,
        format: formatMap[getNumberValue(row.kpiDataType)],
        gridType: 'number',
        valueViewer: undefined, // remove valueViewer meant for row headers
      };
    });
  });

  return cells;
};

export default parser;
